import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { GetRtoCityLocation } from "../../Service/_index";
import { ToastError } from "../../UiComponents/Toaster/Toast";

/**
 * Assign one RTO City/Location to a user.
 *
 * Like AddMakeModelDrawer this does NOT persist on its own — it hands the row
 * back to AddUser, which submits the whole array with the user. There is no
 * per-user value to set (no discount equivalent), so the mapping IS the row.
 *
 * The Online Policy dropdown is restricted to whatever ends up in this list.
 * An EMPTY list means no restriction: the user then sees the whole master.
 */
const AddRtoCityDrawer = ({
  formType,
  selectedRow,
  existingRows = [],
  onSave,
  onClose,
}) => {
  const isEdit = formType === "edit";

  const [rtoOptions, setRtoOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRto, setSelectedRto] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await GetRtoCityLocation();
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        const options = rows
          .filter((item) => item.isActive)
          .map((item) => ({
            _id: item._id,
            RTOCode: item.RTOCode,
            Region: item.Region || "",
            city: item.city || "",
            label: `${item.RTOCode || ""}${item.Region ? ` - ${item.Region}` : ""
              }${item.city ? ` - ${item.city}` : ""}`,
          }));
        setRtoOptions(options);

        // On edit, resolve the stored id back to its option so the (read-only)
        // name can be shown.
        if (isEdit && selectedRow?.values?.rtoCity) {
          const storedId = String(selectedRow.values.rtoCity);
          setSelectedRto(options.find((o) => String(o._id) === storedId) || null);
        }
      } catch (e) {
        if (!cancelled) ToastError("Failed to load RTO City/Location list");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, selectedRow]);

  // RTOs already on this user are left OUT of the dropdown — offering one only
  // to reject it on save ("already in the list") wastes the operator's time.
  // The row being edited keeps its own value so the box is not blanked.
  const selectableOptions = React.useMemo(() => {
    const ownId = isEdit ? String(selectedRow?.values?.rtoCity || "") : "";
    const taken = new Set(
      (existingRows || [])
        .map((row) => String(row.rtoCity))
        .filter((id) => id !== ownId)
    );
    return rtoOptions.filter((option) => !taken.has(String(option._id)));
  }, [rtoOptions, existingRows, isEdit, selectedRow]);

  const handleSave = () => {
    setError("");

    if (!selectedRto) {
      setError("Please select an RTO City/Location");
      return;
    }


    // Assigning the same RTO twice would just duplicate a dropdown entry.
    const clashIndex = existingRows.findIndex(
      (row) => String(row.rtoCity) === String(selectedRto._id)
    );
    if (clashIndex !== -1 && (!isEdit || clashIndex !== selectedRow?.index)) {
      setError("This RTO City/Location is already in the list");
      return;
    }

    onSave(
      {
        rtoCity: selectedRto._id,
        // Kept for display only — the reference above is the source of truth.
        RTOCode: selectedRto.RTOCode,
        Region: selectedRto.Region,
        city: selectedRto.city,
      },
      isEdit ? selectedRow.index : undefined
    );
  };

  return (
    <Box sx={{ pt: 3, px: 3, pb: 0, minWidth: 480 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
          {isEdit ? "Edit RTO City/Location" : "Add RTO City/Location"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography sx={{ mb: 1, fontWeight: 500, fontSize: 13 }}>
            RTO City/Location *
          </Typography>
          <Autocomplete
            // index.scss sets .MuiOutlinedInput-notchedOutline to transparent
            // globally, so MUI's own border never shows. The app draws input
            // borders through these classes instead.
            className="AutoComplete_InputBox"
            // NOT disablePortal: inside a Dialog the inline popup is clipped by
            // the paper and scrolls the dialog instead of overlaying it.
            // Portalling to the body lets the list overlay normally.
            options={selectableOptions}
            noOptionsText={
              rtoOptions.length > 0
                ? "Every RTO City/Location is already added"
                : "No RTO City/Location available"
            }
            loading={loading}
            value={selectedRto}
            // Long "CODE - REGION - CITY" labels otherwise overflow the row.
            ListboxProps={{ style: { maxHeight: 260 } }}
            componentsProps={{
              popper: { style: { zIndex: 1500 } },
            }}
            onChange={(e, v) => {
              setSelectedRto(v);
              setError("");
            }}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) =>
              String(option?._id) === String(value?._id)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select RTO City/Location"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography sx={{ fontSize: 11, color: "#667085", mt: 0.5 }}>
            Only these RTO cities will be selectable for this user on the Online
            Policy form.
          </Typography>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>
              {error}
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Project button theme — same classes every other dialog uses
          (UiComponents/Scss/Buttons.scss: brand navy #1E3A5F). */}
      <div className="Dialog_Footer" style={{ marginTop: 20 }}>
        <Button onClick={onClose} className="Dialog_Cancel">
          Cancel
        </Button>
        <Button onClick={handleSave} className="Dialog_Save_Exit">
          {isEdit ? "Update" : "Add"}
        </Button>
      </div>
    </Box>
  );
};

export default AddRtoCityDrawer;
